/* eslint-disable */
import { Button, Select, Upload } from '@ui';
import React, { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useGetAliasesListQuery } from '@app/store/api/alias-settings-page.api';
import { AliasSettingsObj, GPTAllIntegrationListObjectType } from '@app/interfaces/settings.type';
import { OptionItem } from '../ui/select/select.type';
import {
  AllChanelsArrType,
  MetricClassifierModelsType,
  MetricObjType,
} from '@app/interfaces/pages-types/anatylics-metric.type';
import { Filter, Range } from '../ui/easy-filter/types';
import { useGetAllDictionariesListQuery } from '@app/store/api/dictionaries.api';
import { DictionariesType } from '@app/interfaces/slices-types/dictionaries-slice.type';
import { WordsNGramSettings } from '@app/interfaces/words-n-gramm.type';
import { useGetChannelsQuery } from '@app/store/api/get-channels-info.api';
import { useGetChatRolesQuery } from '@app/store/api/chat-api/get-chat-roles.api';
import { useGetGptIntegrationsListAllQuery } from '@app/store/api/intergation-settings.api';
import { useGetClassifierSecondModelsListQuery } from '@app/store/api/metrics.api';
import { ChatRoleSettingsType } from '@app/interfaces/chat-roles-settings.type';
import { replaceValuesDeep } from './helpers';
import { FinalImportType } from '@app/interfaces/analytics';
import { v4 } from 'uuid';
import { GPTGroupObjectType } from '@app/interfaces/gpt-metric.type';
import { GraphSettings } from '@app/interfaces/report.type';
import { isUndefined } from 'lodash';
import useTranslation from '@app/hooks/use-translation';

type StepsType = 1 | 2 | 3 | 4;
type AdditionalSettingsType = Record<
  'channels' | 'chatRoles' | 'gptIntegrations' | 'classifier2',
  Record<string, string>
>;

type ExternalReferencesKeys =
  | 'aliases'
  | 'channels'
  | 'classifiers_second'
  | 'classifiers_first'
  | 'dictionaries'
  | 'gpt_groups'
  | 'gpt_user_integrations'
  | 'roles'
  | 'gpt_system_integrations';

type formattedFileType = {
  external_references?: Partial<Record<ExternalReferencesKeys, any>>;
  metrics_settings?: MetricObjType[];
  preset?: Filter & Range;
  project_type: 'chat' | 'voice';
  version: string;
  reports?: {
    name?: string;
    description?: string | null;
    filter?: Filter & Range;
    graphs?: any[];
    ngramcloud_settings?: WordsNGramSettings;
  }[];
};
//const
const metricTypesToFindDictionaries = ['c_find-words', 'find-words', 'c_spell-check'];
const stepLabelClassName = 'font-bold text-1color text-[20px] flex w-full justify-center pb-8';
const additional_settings_description_className = 'font-bold text-[18px] text-1color pb-4 block';
//const

const ImportAPFeature: FC<{
  template: 'chat' | 'voice';
  handleImport(data: FinalImportType): void;
  onClose(): void;
}> = ({ template, handleImport, onClose }) => {
  // api
  const { data: aliases } = useGetAliasesListQuery();
  const { data: dictionaries } = useGetAllDictionariesListQuery();
  const { data: channels } = useGetChannelsQuery(undefined, { skip: template === 'chat' });
  const { data: chatRoles } = useGetChatRolesQuery(undefined, { skip: template === 'voice' });
  const { data: gptIntegrations } = useGetGptIntegrationsListAllQuery();
  const { data: classifier2 } = useGetClassifierSecondModelsListQuery();

  // api
  const [disable, setDisable] = useState(true);

  //data fill
  const [participatingAliases, setParticipatingAliases] = useState<Record<string, string>>({});
  const [participatingDictionaries, setParticipatingDictionaries] = useState<
    Record<string, string>
  >({});
  const [participatingAdditionalSettings, setParticipatingAdditionalSettings] =
    useState<AdditionalSettingsType>({
      channels: {},
      chatRoles: {},
      classifier2: {},
      gptIntegrations: {},
    });

  function generateUUID() {
    return v4();
  }

  function handleMakeImport() {
    if (!formattedFile) return;

    const replacedGptGroups = (formattedFile?.external_references?.gpt_groups as any[])?.reduce<
      FinalImportType['gpt_groups']
    >((acc, group) => {
      const uuid = generateUUID();

      let settings = { ...group.settings };

      if (settings.gpt_integration.type === 'user') {
        settings.gpt_integration.gpt_integration_id =
          participatingAdditionalSettings.gptIntegrations[
            settings.gpt_integration.gpt_integration_id
          ];
      }

      if (template === 'voice' && settings.channel && settings.channel !== 'all') {
        settings.channel = participatingAdditionalSettings.channels?.[settings.channel];
      }

      if (template === 'chat' && settings.c_role && settings.c_role !== 'all') {
        settings.c_role = participatingAdditionalSettings.chatRoles?.[settings.c_role];
      }

      // TODO: dont forget about not user integration
      acc[uuid] = { ...group, settings };
      return acc;
    }, {});

    // helper
    const groupByIdMap = Object.entries(replacedGptGroups || {}).reduce<Record<string, string>>(
      (acc, [key, value]) => {
        acc[value.name ?? ''] = key;
        return acc;
      },
      {},
    );
    // helper

    const replacedMetricSettings = formattedFile?.metrics_settings?.reduce<
      FinalImportType['metrics_settings']
    >((acc, metric) => {
      const uuid = generateUUID();
      let settings = { ...metric.settings };

      if (metric.type === 'meta') {
        settings.alias_id = participatingAliases[settings.alias_id ?? ''];
      }

      if (metric.type === 'classifier2') {
        settings.model_id = participatingAdditionalSettings.classifier2?.[settings.model_id ?? ''];
      }
      if (metric.type === 'gpt') {
        const name = (settings.gpt_group as unknown as GPTGroupObjectType)?.name
          ? (settings.gpt_group as unknown as GPTGroupObjectType)?.name
          : settings.gpt_group || 'groupNameError';
        settings.gpt_group = groupByIdMap[name];
      }

      if (metricTypesToFindDictionaries.includes(metric.type)) {
        settings.dictionaries =
          settings.dictionaries?.map((dict) => participatingDictionaries[dict]) ?? [];
      }

      if (template === 'voice' && settings.channel && settings.channel !== 'all') {
        settings.channel = participatingAdditionalSettings.channels?.[settings.channel];
      }

      if (template === 'chat' && settings.c_role && settings.c_role !== 'all') {
        settings.c_role = participatingAdditionalSettings.chatRoles?.[settings.c_role];
      }

      acc[uuid] = {
        ...metric,
        settings,
      };

      return acc;
    }, {});

    // helper
    const metricByIdMap = Object.entries(replacedMetricSettings || {}).reduce<
      Record<string, string>
    >((acc, [key, value]) => {
      acc[value.name ?? ''] = key;
      return acc;
    }, {});

    // helper

    const replacedPresetFilter = formattedFile?.preset?.filter?.map((item) => {
      if (Array.isArray(item)) {
        return item.map((nested) => ({ ...nested, id: participatingAliases[nested.id] }));
      } else return { ...item, id: participatingAliases[item.id] };
    });

    const replacedReports = formattedFile?.reports?.map((report) => {
      return {
        ...report,
        // ngram
        ngramcloud_settings: {
          ...report?.ngramcloud_settings,
          // channels:
          //   template === 'voice'
          //     ? report?.ngramcloud_settings?.channels?.map((channel) =>
          //         channel === 'all' ? 'all' : participatingAdditionalSettings?.channels?.[channel],
          //       )
          //     : undefined,
          // c_roles:
          //   template === 'chat'
          //     ? report.ngramcloud_settings?.c_roles?.map((role) =>
          //         role === 'all' ? 'all' : participatingAdditionalSettings?.chatRoles?.[role],
          //       )
          //     : undefined,
          stopwords: {
            ...report?.ngramcloud_settings?.stopwords,
            dictionaries: report?.ngramcloud_settings?.stopwords?.dictionaries?.map(
              (dict) => participatingDictionaries?.[dict],
            ),
          },
          startpoint: {
            ...report?.ngramcloud_settings?.startpoint,
            find: {
              ...report?.ngramcloud_settings?.startpoint.find,
              dictionaries: report?.ngramcloud_settings?.startpoint?.find?.dictionaries?.map(
                (dict) => participatingDictionaries?.[dict],
              ),
            },
          },
        },
        // ngram
        // filter hight
        filter: {
          ...report.filter,
          filter: report?.filter?.filter.map((filter) => {
            if (Array.isArray(filter)) {
              return filter.map((nested) => ({ ...nested, id: metricByIdMap?.[nested.id] }));
            } else {
              return { ...filter, id: metricByIdMap?.[filter.id] };
            }
          }),
        },
        // filter hight
        // charts
        graphs: report?.graphs?.map((graph) => {
          return {
            ...graph,
            settings: {
              ...graph.settings,
              data: graph.settings.data?.map((point) => {
                if (!point.metric_id) return point;

                return {
                  ...point,
                  metric_id: metricByIdMap[point.metric_id],
                };
              }),
              filter: graph.settings?.filter?.map((filter) => ({
                ...filter,
                id: metricByIdMap[filter.id],
              })),
              group: {
                ...graph.settings.group,
                metric_id: graph.settings.group?.metric_id
                  ? Array.isArray(graph.settings.group?.metric_id)
                    ? graph.settings.group?.metric_id.map((name) => metricByIdMap[name])
                    : metricByIdMap[graph.settings.group.metric_id]
                  : undefined,
              },
            },
          };
        }),
        // charts
      };
    });

    const target = {
      metrics_settings: Object.fromEntries(
        Object.entries(replacedMetricSettings || {}).map(([id, settings]) => {
          if (settings.type === 'calculated') {
            return [
              id,
              {
                ...settings,
                settings: {
                  ...settings.settings,
                  formula: replaceValuesDeep(settings.settings.formula, metricByIdMap),
                },
              },
            ];
          } else if (settings.type === 'tags') {
            return [
              id,
              {
                ...settings,
                settings: {
                  ...settings.settings,
                  default: {
                    ...settings.settings?.default,
                    copy_from: settings.settings?.default?.copy_from
                      ? metricByIdMap[settings.settings?.default?.copy_from]
                      : undefined,
                  },
                },
              },
            ];
          } else if (settings.type === 'gpt') {
            return [
              id,
              {
                ...settings,
                settings: {
                  ...settings.settings,
                  condition: {
                    ...settings.settings.condition,
                    filter: (settings.settings.condition?.filter || []).map((filter) => {
                      if (Array.isArray(filter)) {
                        return filter.map((nested) => ({
                          ...nested,
                          id: metricByIdMap?.[nested.id],
                        }));
                      } else {
                        return { ...filter, id: metricByIdMap?.[filter.id] };
                      }
                    }),
                  },
                },
              },
            ];
          } else if (settings.type === 'find-words') {
            return [
              id,
              {
                ...settings,
                settings: {
                  ...settings.settings,
                  relations: (settings.settings?.relations || []).map((relation) => ({
                    ...relation,
                    metric_id: metricByIdMap?.[relation?.metric_id] || '',
                  })),
                },
              },
            ];
          } else if (settings.type === 'c_find-words') {
            return [
              id,
              {
                ...settings,
                settings: {
                  ...settings.settings,
                  relations: (settings.settings?.relations || []).map((relation) => ({
                    ...relation,
                    metric_id: metricByIdMap?.[relation?.metric_id] || '',
                  })),
                },
              },
            ];
          }
          return [id, settings];
        }),
      ),
      gpt_groups: replacedGptGroups,
      preset: formattedFile.preset
        ? { ...formattedFile.preset, filter: replacedPresetFilter }
        : undefined,
      reports: replacedReports,
    } as FinalImportType;
    handleImport(target);
    onClose();
  }

  const [step, setStep] = useState<StepsType>(1);
  const [formattedFile, changeFormattedFile] = useState<formattedFileType>();

  const markup: Record<StepsType, ReactNode> = {
    1: <FirstStepMarkup setDisable={setDisable} changeFormattedFile={changeFormattedFile} />,
    2: (
      <SecondStepMarkup
        aliases={aliases}
        formattedFile={formattedFile}
        participatingAliases={participatingAliases}
        setParticipatingAliases={setParticipatingAliases}
      />
    ),
    3: (
      <ThirdStepMarkup
        dictionaries={dictionaries}
        participatingDictionaries={participatingDictionaries}
        formattedFile={formattedFile}
        setParticipatingDictionaries={setParticipatingDictionaries}
      />
    ),
    4: (
      <FourStepMarkup
        formattedFile={formattedFile}
        channels={channels}
        chatRoles={chatRoles}
        gptIntegrations={gptIntegrations}
        classifier2={classifier2}
        participatingAdditionalSettings={participatingAdditionalSettings}
        setParticipatingAdditionalSettings={setParticipatingAdditionalSettings}
      />
    ),
  };
  const stepChangeMap: Record<StepsType, StepsType> = { 1: 2, 2: 3, 3: 4, 4: 4 };
  return (
    <div className="relative pt-[20px] flex flex-col h-[80vh] justify-center  w-full">
      {markup[step]}
      <CommonButtonBlock
        disableNextButton={disable}
        onClose={onClose}
        step={step}
        onNextClick={() =>
          setStep((prev) => {
            return stepChangeMap[prev];
          })
        }
        handleMakeImport={handleMakeImport}
      />
    </div>
  );
};

export default ImportAPFeature;

const CommonButtonBlock = ({
  disableNextButton,
  onNextClick,
  step,
  onClose,
  handleMakeImport,
}: {
  disableNextButton?: boolean;
  handleMakeImport(): void;
  onClose(): void;
  onNextClick?(): void;
  step: StepsType;
}) => {
  const { t } = useTranslation('components.importExportAp');

  return (
    <div className="sticky bottom-0 z-[10] bg-white pt-5 pb-3">
      <div className="flex items-center justify-between">
        <Button onClick={onClose} label={t('cancel_button')} fill="linked" />
        <Button
          label={step === 4 ? t('next_button_final') : t('next_button')}
          disabled={disableNextButton}
          onClick={step === 4 ? handleMakeImport : onNextClick}
        />
      </div>
    </div>
  );
};

const FirstStepMarkup = ({
  changeFormattedFile,
  setDisable,
}: {
  changeFormattedFile: React.Dispatch<React.SetStateAction<any>>;
  setDisable: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { t } = useTranslation('components.importExportAp');

  const [projectFile, changeFile] = useState<File[]>([]);
  const [jsonValidationError, setJsonValidationError] = useState<string>();

  async function validateImport(file: File) {
    try {
      setJsonValidationError(undefined);
      const text = await file.text();

      const json = JSON.parse(text);

      setDisable(false);
      return json;
    } catch {
      setJsonValidationError(t('json_validation_error'));
    }
  }

  async function handleChangeFile(file: File[]) {
    if (file.length <= 0) {
      setDisable(true);
      return;
    }
    const validatedFile = await validateImport(file[0]);
    if (!validatedFile) return;
    changeFormattedFile(validatedFile);
  }

  return (
    <div className="h-full">
      <h4 className={stepLabelClassName}>{t('upload_label')}</h4>

      <div>
        <Upload
          multiple={false}
          loadedFiles={projectFile}
          changeLoadedFiles={changeFile}
          onChange={handleChangeFile}
        />
      </div>
      {jsonValidationError && projectFile.length > 0 && (
        <span className="py-2 text-basic_red text-[14px] font-medium">{jsonValidationError}</span>
      )}
    </div>
  );
};

const SecondStepMarkup = ({
  formattedFile,
  aliases,
  participatingAliases,
  setParticipatingAliases,
}: {
  participatingAliases: Record<string, string>;
  setParticipatingAliases: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  formattedFile: formattedFileType | undefined;
  aliases:
    | {
        aliases: AliasSettingsObj[];
      }
    | undefined;
}) => {
  const { t } = useTranslation('components.importExportAp');

  const usedAliases = useMemo(
    () => (formattedFile?.external_references?.aliases as string[]) ?? [],
    [formattedFile],
  );

  const options: OptionItem[] =
    aliases?.aliases.map((alias) => ({
      value: alias.alias_id,
      title: alias.name,
    })) ?? [];

  const findAliasByName = useCallback(
    (name: string) => {
      return aliases?.aliases.find((alias) => alias.name === name)?.alias_id;
    },
    [aliases],
  );

  useEffect(() => {
    if (!usedAliases?.length) return;

    const map: Record<string, string> = {};

    usedAliases.forEach((name) => {
      map[name] = findAliasByName(name) ?? '';
    });

    setParticipatingAliases(map);
  }, [usedAliases, findAliasByName, setParticipatingAliases]);

  const findBelongingMetrics = useCallback(
    (aliasName: string): string[] => {
      const result: string[] = [];

      formattedFile?.metrics_settings?.forEach((metric) => {
        if (metric?.type === 'meta' && metric.settings.alias_id === aliasName) {
          result.push(metric.name);
        }
      });

      const existsInPreset =
        formattedFile?.preset?.filter?.some((filterElem) => {
          if (Array.isArray(filterElem)) {
            return filterElem.some((filter) => filter.id === aliasName);
          }

          return filterElem?.id === aliasName;
        }) ?? false;

      if (existsInPreset) {
        result.push('Пресет');
      }

      return result;
    },
    [formattedFile],
  );

  return (
    <div className="h-full">
      <h4 className={stepLabelClassName}>{t('alias_label')}</h4>
      <div className="h-full flex flex-col gap-[10px]">
        {usedAliases.map((name, index) => {
          return (
            <div key={`${name}-${index}`} className="grid grid-cols-3 grid-rows-1 gap-[20px]">
              <div className="truncate">{name}</div>
              <div>
                <Select
                  defaultValue={participatingAliases[name]}
                  options={options}
                  onChange={(val) => {
                    const value = String(val);
                    setParticipatingAliases((prev) => {
                      const copy = { ...prev };
                      copy[name] = value;
                      return copy;
                    });
                  }}
                />
              </div>
              <div className="flex flex-wrap gap-[5px]">
                {findBelongingMetrics(name)?.map((i, metricIndex) => (
                  <div
                    key={`${i}-${index}-${metricIndex}`}
                    className="text-3color h-fit w-fit font-medium text-[13px] p-1 rounded-[4px] bg-b_light"
                  >
                    {i}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ThirdStepMarkup = ({
  dictionaries,
  participatingDictionaries,
  setParticipatingDictionaries,
  formattedFile,
}: {
  dictionaries: DictionariesType[] | undefined;
  participatingDictionaries: Record<string, string>;
  setParticipatingDictionaries: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  formattedFile: formattedFileType | undefined;
}) => {
  const { t } = useTranslation('components.importExportAp');

  const usedDictionaries = useMemo(
    () => (formattedFile?.external_references?.dictionaries as string[]) ?? [],
    [formattedFile],
  );

  const options: OptionItem[] =
    dictionaries?.map((dict) => ({
      value: dict.dictionary_id,
      title: dict.name,
    })) || [];

  const findDictionaryByName = useCallback(
    (name: string) => {
      return dictionaries?.find((dict) => dict.name === name)?.dictionary_id;
    },
    [dictionaries],
  );

  useEffect(() => {
    if (!usedDictionaries?.length) return;

    const map: Record<string, string> = {};

    usedDictionaries.forEach((name) => {
      map[name] = findDictionaryByName(name) ?? '';
    });

    setParticipatingDictionaries(map);
  }, [usedDictionaries, findDictionaryByName, setParticipatingDictionaries]);

  const findBelongingMetrics = useCallback(
    (dictName: string): string[] => {
      const result: string[] = [];

      formattedFile?.metrics_settings?.forEach((metric) => {
        if (
          metricTypesToFindDictionaries.includes(metric?.type ?? '') &&
          metric?.settings?.dictionaries?.includes(dictName)
        ) {
          result.push(metric.name);
        }
      });

      const existsInReports =
        formattedFile?.reports?.some((report) =>
          report?.ngramcloud_settings?.stopwords?.dictionaries?.includes(dictName),
        ) ?? false;

      if (existsInReports) {
        result.push('Облако фраз');
      }

      return result;
    },
    [formattedFile],
  );

  return (
    <div className="h-full">
      <h4 className={stepLabelClassName}>{t('dictionaries_label')}</h4>

      <div className="h-full flex flex-col gap-[10px]">
        {usedDictionaries.map((name, index) => {
          return (
            <div key={`${name}-${index}`} className="grid grid-cols-3 grid-rows-1 gap-[20px]">
              <div className="truncate">{name}</div>
              <div>
                <Select
                  defaultValue={participatingDictionaries[name]}
                  options={options}
                  onChange={(val) => {
                    const value = String(val);
                    setParticipatingDictionaries((prev) => {
                      const copy = { ...prev };
                      copy[name] = value;
                      return copy;
                    });
                  }}
                />
              </div>
              <div className="flex flex-wrap gap-[5px]">
                {findBelongingMetrics(name)?.map((i, metricIndex) => (
                  <div
                    key={`${i}-${index}-${metricIndex}`}
                    className="text-3color h-fit w-fit font-medium text-[13px] p-1 rounded-[4px] bg-b_light"
                  >
                    {i}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const FourStepMarkup = ({
  channels,
  chatRoles,
  classifier2,
  formattedFile,
  gptIntegrations,
  participatingAdditionalSettings,
  setParticipatingAdditionalSettings,
}: {
  channels: AllChanelsArrType[] | undefined;
  chatRoles: ChatRoleSettingsType[] | undefined;
  classifier2: MetricClassifierModelsType[] | undefined;
  gptIntegrations: GPTAllIntegrationListObjectType | undefined;
  formattedFile: formattedFileType | undefined;
  participatingAdditionalSettings: AdditionalSettingsType;
  setParticipatingAdditionalSettings: React.Dispatch<React.SetStateAction<AdditionalSettingsType>>;
}) => {
  const { t } = useTranslation('components.importExportAp');

  const usedChannels = useMemo(
    () => (formattedFile?.external_references?.channels as string[]) ?? [],
    [formattedFile],
  );
  const usedChatRoles = useMemo(
    () => (formattedFile?.external_references?.roles as string[]) ?? [],
    [formattedFile],
  );
  const usedClassifier2 = useMemo(
    () => (formattedFile?.external_references?.classifiers_second as string[]) ?? [],
    [formattedFile],
  );
  const usedGptIntegrations = useMemo(
    () => (formattedFile?.external_references?.gpt_user_integrations as string[]) ?? [],
    [formattedFile],
  );

  useEffect(() => {
    const map: AdditionalSettingsType = {
      channels: {},
      chatRoles: {},
      classifier2: {},
      gptIntegrations: {},
    };
    if (usedChannels) {
      usedChannels.forEach((name) => {
        const id = channels?.find((channel) => channel.name === name)?.settings_channels_id ?? '';
        map['channels'][name] = id + '';
      });
    }

    if (usedChatRoles) {
      usedChatRoles.forEach((name) => {
        const id = chatRoles?.find((role) => role.name === name)?.crole_id ?? '';
        map['chatRoles'][name] = id;
      });
    }

    if (usedClassifier2) {
      usedClassifier2.forEach((name) => {
        const id = classifier2?.find((classifier) => classifier.name === name)?.model_id ?? '';
        map['classifier2'][name] = id;
      });
    }

    if (usedGptIntegrations) {
      usedGptIntegrations.forEach((name) => {
        const id =
          gptIntegrations?.gpt_integrations?.find((integration) => integration.description === name)
            ?.gpt_integration_id ?? '';
        map['gptIntegrations'][name] = id;
      });
    }

    setParticipatingAdditionalSettings(map);
  }, []);

  const channelOptions =
    channels?.map((channel) => ({
      value: channel.settings_channels_id,
      title: channel.name,
    })) || [];
  const chatRolesOptions =
    chatRoles?.map((role) => ({
      value: role.crole_id || '',
      title: role.name || '',
    })) || [];
  const classifier2Options =
    classifier2?.map((classifier) => ({
      value: classifier.model_id || '',
      title: classifier.name || '',
    })) || [];
  const gptIntegrationsOptions =
    gptIntegrations?.gpt_integrations.map((integration) => ({
      value: integration.gpt_integration_id || '',
      title: integration.description || '',
    })) || [];

  return (
    <div className="h-full">
      <h4 className={stepLabelClassName}>{t('additional_settings_label')}</h4>
      {/* channels */}
      <div className="flex flex-col gap-[20px]">
        {channels && !!usedChannels.length && (
          <div>
            <span className={additional_settings_description_className}>{t('channels')}</span>
            <div className="flex flex-col gap-[10px]">
              {usedChannels.map((name, index) => {
                return (
                  <div key={`${name}-${index}`} className="grid grid-cols-3 grid-rows-1 gap-[20px]">
                    <div className="truncate">{name}</div>
                    <div>
                      <Select
                        defaultValue={participatingAdditionalSettings?.['channels']?.[name]}
                        options={channelOptions}
                        onChange={(val) => {
                          const value = String(val);
                          setParticipatingAdditionalSettings((prev) => ({
                            ...prev,
                            channels: {
                              ...(prev?.channels || {}),
                              [name]: value,
                            },
                          }));
                        }}
                      />
                    </div>
                    <div className="flex flex-wrap gap-[5px]"></div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* channels */}
        {/* chatroles */}
        {chatRoles && !!usedChatRoles.length && (
          <div>
            <span className={additional_settings_description_className}>{t('c_roles')}</span>
            <div className="flex flex-col gap-[10px]">
              {usedChatRoles.map((name, index) => {
                return (
                  <div key={`${name}-${index}`} className="grid grid-cols-3 grid-rows-1 gap-[20px]">
                    <div className="truncate">{name}</div>
                    <div>
                      <Select
                        defaultValue={participatingAdditionalSettings?.['chatRoles']?.[name]}
                        options={chatRolesOptions}
                        onChange={(val) => {
                          const value = String(val);
                          setParticipatingAdditionalSettings((prev) => ({
                            ...prev,
                            chatRoles: {
                              ...(prev?.chatRoles || {}),
                              [name]: value,
                            },
                          }));
                        }}
                      />
                    </div>
                    <div className="flex flex-wrap gap-[5px]"></div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* chatroles */}
        {/* gptIntegrations */}
        {gptIntegrations && !!usedGptIntegrations.length && (
          <div>
            <span className={additional_settings_description_className}>
              {t('gpt_integrations_user')}
            </span>
            <div className="flex flex-col gap-[10px]">
              {usedGptIntegrations.map((name, index) => {
                return (
                  <div key={`${name}-${index}`} className="grid grid-cols-3 grid-rows-1 gap-[20px]">
                    <div className="truncate">{name}</div>
                    <div>
                      <Select
                        defaultValue={participatingAdditionalSettings?.['gptIntegrations']?.[name]}
                        options={gptIntegrationsOptions}
                        onChange={(val) => {
                          const value = String(val);
                          setParticipatingAdditionalSettings((prev) => ({
                            ...prev,
                            gptIntegrations: {
                              ...(prev?.gptIntegrations || {}),
                              [name]: value,
                            },
                          }));
                        }}
                      />
                    </div>
                    <div className="flex flex-wrap gap-[5px]"></div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* gptIntegrations */}
        {/* classifier2 */}
        {classifier2 && !!usedClassifier2.length && (
          <div>
            <span className={additional_settings_description_className}>{t('classifier2')}</span>
            <div className="flex flex-col gap-[10px]">
              {usedClassifier2.map((name, index) => {
                return (
                  <div key={`${name}-${index}`} className="grid grid-cols-3 grid-rows-1 gap-[20px]">
                    <div className="truncate">{name}</div>
                    <div>
                      <Select
                        defaultValue={participatingAdditionalSettings?.['classifier2']?.[name]}
                        options={classifier2Options}
                        onChange={(val) => {
                          const value = String(val);
                          setParticipatingAdditionalSettings((prev) => ({
                            ...prev,
                            classifier2: {
                              ...(prev?.classifier2 || {}),
                              [name]: value,
                            },
                          }));
                        }}
                      />
                    </div>
                    <div className="flex flex-wrap gap-[5px]"></div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* classifier2 */}
      </div>
    </div>
  );
};
